import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, Send, Eye, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const WEEKDAYS = [["0", "Lunedì"], ["1", "Martedì"], ["2", "Mercoledì"], ["3", "Giovedì"], ["4", "Venerdì"], ["5", "Sabato"], ["6", "Domenica"]];
const asList = (s) => (s || "").split(/[\n,;]+/).map((x) => x.trim()).filter((x) => x.includes("@"));

export default function AffidamentiFunnelDialog({ open, onOpenChange }) {
  const [cfg, setCfg] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [summary, setSummary] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [recipientsText, setRecipientsText] = useState("");
  const [ccText, setCcText] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true); setSummary(null);
    api.get("/affidamenti-funnel-config").then((r) => {
      setCfg(r.data.config); setCanEdit(!!r.data.can_edit);
      setRecipientsText((r.data.config.recipients || []).join("\n"));
      setCcText((r.data.config.cc || []).join(", "));
    }).catch(() => toast.error("Errore caricamento configurazione")).finally(() => setLoading(false));
  }, [open]);

  const upd = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const save = async () => {
    setBusy("save");
    try {
      const r = await api.put("/affidamenti-funnel-config", { config: cfg });
      setCfg(r.data.config);
      toast.success("Configurazione salvata");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setBusy(""); }
  };

  const anteprima = async () => {
    setBusy("preview");
    try {
      const r = await api.post("/affidamenti-funnel-config/anteprima", {});
      setSummary({ clienti: r.data.tot_clienti, oltre: r.data.n_oltre_fido, no_bil: r.data.n_no_bilancio });
      setPreviewHtml(r.data.html || "<p style='font-family:sans-serif;padding:16px'>Nessun contenuto.</p>");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore anteprima"); }
    finally { setBusy(""); }
  };

  const inviaProva = async () => {
    setBusy("test");
    try {
      const r = await api.post("/affidamenti-funnel-config/invia-prova", { email: testEmail || undefined });
      toast.success(`Prova inviata a ${r.data.to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Invio prova fallito"); }
    finally { setBusy(""); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="aff-funnel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-primary" /> Funnel Affidamenti</DialogTitle>
            <DialogDescription>Report automatico con riepilogo esposizioni, clienti fuori affidamento e allerta bilanci mancanti.</DialogDescription>
          </DialogHeader>
          {loading || !cfg ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-md border border-input p-2.5">
                <Label className="text-sm font-medium">Invio automatico attivo</Label>
                <Switch checked={!!cfg.enabled} onCheckedChange={(v) => upd({ enabled: v })} disabled={!canEdit} data-testid="aff-funnel-enabled" />
              </div>

              <div>
                <Label className="text-xs">Oggetto email</Label>
                <Input value={cfg.subject || ""} onChange={(e) => upd({ subject: e.target.value })} disabled={!canEdit} data-testid="aff-funnel-subject" className="h-9" />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Destinatari (uno per riga)</Label>
                </div>
                <textarea className="w-full rounded-md border border-input bg-background p-2 text-sm" rows={2} data-testid="aff-funnel-recipients" disabled={!canEdit}
                  value={recipientsText} onChange={(e) => { setRecipientsText(e.target.value); upd({ recipients: asList(e.target.value) }); }} placeholder="amministrazione@torrisolare.it" />
              </div>
              <div>
                <Label className="text-xs">In copia (CC)</Label>
                <Input value={ccText} data-testid="aff-funnel-cc" disabled={!canEdit} onChange={(e) => { setCcText(e.target.value); upd({ cc: asList(e.target.value) }); }} placeholder="cc1@..., cc2@..." className="h-9" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Frequenza</Label>
                  <Select value={cfg.frequency} onValueChange={(v) => upd({ frequency: v })} disabled={!canEdit}>
                    <SelectTrigger data-testid="aff-funnel-frequency" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="daily">Giornaliera</SelectItem><SelectItem value="weekly">Settimanale</SelectItem><SelectItem value="biweekly">Bisettimanale</SelectItem><SelectItem value="monthly">Mensile (1° del mese)</SelectItem></SelectContent>
                  </Select>
                </div>
                {(cfg.frequency === "weekly" || cfg.frequency === "biweekly") && (
                  <div>
                    <Label className="text-xs">Giorno</Label>
                    <Select value={String(cfg.weekday)} onValueChange={(v) => upd({ weekday: Number(v) })} disabled={!canEdit}>
                      <SelectTrigger data-testid="aff-funnel-weekday" className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{WEEKDAYS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-xs">Orario</Label>
                  <Input type="time" value={cfg.send_time} onChange={(e) => upd({ send_time: e.target.value })} disabled={!canEdit} data-testid="aff-funnel-time" className="h-9" />
                </div>
              </div>

              <div className="flex items-center justify-between rounded-md border border-input p-2.5">
                <div>
                  <Label className="text-xs">Invia solo in presenza di allerte</Label>
                  <p className="text-[11px] text-muted-foreground">Nessuna email se non ci sono sconfini né bilanci mancanti.</p>
                </div>
                <Switch checked={!!cfg.only_alerts} onCheckedChange={(v) => upd({ only_alerts: v })} disabled={!canEdit} data-testid="aff-funnel-only-alerts" />
              </div>

              {summary && (
                <div className="rounded-md border border-primary/30 bg-primary/5 p-2.5 text-xs text-muted-foreground" data-testid="aff-funnel-summary">
                  {summary.clienti} clienti dilazionati · <b className="text-red-500">{summary.oltre}</b> fuori affidamento · <b className="text-amber-500">{summary.no_bil}</b> senza bilancio
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={anteprima} disabled={busy === "preview"} data-testid="aff-funnel-preview">
                  {busy === "preview" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Eye className="mr-1.5 h-4 w-4" />} Anteprima
                </Button>
                <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="email prova" className="h-9 w-40" data-testid="aff-funnel-test-email" />
                <Button type="button" variant="outline" size="sm" onClick={inviaProva} disabled={busy === "test"} data-testid="aff-funnel-test-send">
                  {busy === "test" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />} Invia prova
                </Button>
                {canEdit && (
                  <Button type="button" size="sm" onClick={save} disabled={busy === "save"} className="ml-auto" data-testid="aff-funnel-save">
                    {busy === "save" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Salva
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewHtml} onOpenChange={(o) => { if (!o) setPreviewHtml(null); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden p-0" data-testid="aff-funnel-preview-dialog">
          <DialogHeader className="border-b border-border p-4">
            <DialogTitle className="flex items-center gap-2"><Eye className="h-5 w-5 text-primary" /> Anteprima email — Affidamenti</DialogTitle>
            <DialogDescription>{summary ? `${summary.clienti} clienti · ${summary.oltre} fuori fido · ${summary.no_bil} senza bilancio` : "Anteprima del report"}</DialogDescription>
          </DialogHeader>
          <iframe title="Anteprima Affidamenti" srcDoc={previewHtml || ""} data-testid="aff-funnel-preview-iframe" className="h-[70vh] w-full border-0 bg-white" sandbox="" />
        </DialogContent>
      </Dialog>
    </>
  );
}
